import React, { useContext, useMemo } from 'react'

import { OrgUnitContext } from '@percept/app-components'

import {
  Box,
  Loader,
  Money,
  useAppTheme,
} from '@percept/mui'

import { DashboardCard } from 'components/DashboardCard'

import { BreakdownValueFormatter, ProviderBreakdown, ProviderChartData } from '../ProviderBreakdown'

import { useTargetCurrency } from 'hooks'

import { OrgUnitHomePageContext } from 'contexts'

import { getDifferenceByProvider, useOrgUnitPerformance } from './lib'

import { sortBy, sum } from 'lodash-es'

import { providerLabelMap, reportProvidersV2 } from '@percept/constants'

import { ReportProviderV2 } from '@percept/types'

import { errorContent } from '../common'

import { DashboardHomeCardProps } from '../typings'


export const OrgUnitPerformanceSummary = (props: DashboardHomeCardProps): JSX.Element => {

  const orgUnit = useContext(OrgUnitContext)

  const [{ range: dateRangePreset }] = useContext(OrgUnitHomePageContext)

  const [currency] = useTargetCurrency()

  const valueFormatter: BreakdownValueFormatter = (amount) => (
    <Money amount={amount} abbreviate currency={currency} />
  )

  const { performanceQuery, comparisonPerformanceQuery } = useOrgUnitPerformance({
    orgUnit,
    dateRangePreset,
    currency,
    aggregateToSingleOrgUnit: true,
    includePreviousPeriod: true,
  })

  const appTheme = useAppTheme()

  const derivedData = useMemo(() => {
    if( !(performanceQuery.data && comparisonPerformanceQuery.data) ){
      return null
    }
    const differenceByProvider = getDifferenceByProvider(
      comparisonPerformanceQuery.data,
      performanceQuery.data,
    )
    const colourScale = appTheme.chart.getOrdinalColourScale(reportProvidersV2)
    const chartData: ProviderChartData = Object.entries(differenceByProvider).reduce( (acc, [provider, data]) => {
      let start = null, end = null
      if( data.length === 1 ){
        end = data[0]
      }else{
        start = data[0]
        end = data[1]
      }
      if( end ){
        acc.push({
          label: providerLabelMap[provider as ReportProviderV2],
          provider: provider as ReportProviderV2,
          value: end.metrics.cost,
          previousValue: start ? start.metrics.cost : null,
          color: colourScale(provider as ReportProviderV2),
        })
      }
      return acc
    }, [] as ProviderChartData)

    return {
      chartData: sortBy(chartData, d => reportProvidersV2.indexOf(d.provider)),
      overallTotal: sum(chartData.map( d => d.value || 0)),
      previousOverallTotal: sum(chartData.map( d => d.previousValue || 0)),
    }
  }, [performanceQuery.data, comparisonPerformanceQuery.data, appTheme])

  return (
    <DashboardCard
      title='Media Spend'
      tooltipContent='A summary of your media spend over the selected period.'
      route={`/dashboards/${orgUnit.id}/data-warehouse`}
      isRefetching={performanceQuery.isRefetching || comparisonPerformanceQuery.isRefetching}
      {...props}>
      { !performanceQuery.data && performanceQuery.error || !comparisonPerformanceQuery.data && comparisonPerformanceQuery.error ? (
        errorContent
      ) : !derivedData ? (
        <Loader preset='centered' minHeight='10rem' />
      ) : (
        <Box p={2}>
          <ProviderBreakdown
            {...derivedData}
            formatter={valueFormatter} />
        </Box>
      )}
    </DashboardCard>
  )
}
